import { Injectable } from "@angular/core";
import { WizardModalComponent } from "@app/wizard-setup/wizard-modal/wizard-modal.component";
import { NbDialogRef, NbDialogService } from "@nebular/theme";
import { BehaviorSubject, Subject, interval, of } from "rxjs";
import { HttpUtilService } from "./http-util.service";
import { EnvironmentService } from "./environment.service";
import {
  catchError,
  tap,
  map,
  take,
  retry,
  switchMap,
  takeUntil,
  delay,
} from "rxjs/operators";
import { ColorsService } from "./colors.service";
import { NotificationsService } from "./notifications.service";
import { from } from "rxjs";
import { concatMap, toArray } from "rxjs/operators";
import cloneDeep from "lodash/cloneDeep";
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { WizardCheckToCloseModalComponent } from "@app/wizard-setup/wizard-check-to-close-modal/wizard-check-to-close-modal.component";
import { WebsocketService } from "./websocket.service";
import { IntegrationService } from "@app/admin/services/integration.service";
import { TranslationService } from "./translation.service";
import isEqual from "lodash/isEqual";

@Injectable({
  providedIn: "root",
})
export class SetupWizardService {
  private termsParams = {
    pageNumber: 0,
    perPage: 5,
    searchParam: "name",
    searchTerm: "",
    orderBy: "name",
    direction: "ASC",
    queryKeys: "",
    queryValues: "",
    queryOperations: "available",
    status: "",
  };
  public usersFormArray: FormArray = this.fb.array([]);
  public termsFormArray: FormArray = this.fb.array([]);
  public _instanceDetails = new BehaviorSubject({});
  public integration;
  public previousIntegrations = [];
  public currentPollingProcesses = {};
  public _integrationInput = new BehaviorSubject({
    isProcessing: false,
    hasUpdates: false,
    hasErrors: false,
    hasVisited: false,
    input: new FormGroup({
      url: new FormControl("", [
        Validators.required,
        Validators.pattern('^(https)://[^ "]+$'),
      ]),
      friendlyName: new FormControl("", [Validators.required]),
      username: new FormControl("", [Validators.required]),
      password: new FormControl("", [Validators.required]),
      termsProcess: new FormControl({
        status: "processing",
      }),
      tokensProcess: new FormControl({
        status: "processing",
      }),
    }),
    status: "not_setup",
  });

  public _themeInput = new BehaviorSubject({
    isLoading: true,
    isProcessing: false,
    hasUpdates: false,
    hasErrors: false,
    hasVisited: false,
    input: {},
  });
  public _usersInput = new BehaviorSubject({
    isProcessing: false,
    hasUpdates: false,
    hasErrors: false,
    hasVisited: false,
    input: this.usersFormArray,
  });

  public _termsInput = new BehaviorSubject({
    itemCount: 0,
    isLoading: false,
    isProcessing: false,
    hasUpdates: false,
    hasErrors: false,
    hasVisited: false,
    list: this.termsFormArray as any,
  });
  public _integrations = new BehaviorSubject({
    list: [],
    isLoading: true,
    selectedIntegration: null,
  });
  public integrations$ = this._integrations.asObservable();
  public integrationInput$ = this._integrationInput.asObservable();
  public themeInput$ = this._themeInput.asObservable();
  public usersInput$ = this._usersInput.asObservable();
  public termsInput$ = this._termsInput.asObservable();
  public loadInstanceDetails = true;
  public loadIntegrations = true;
  public loadTerms = true;
  public dialogRef: NbDialogRef<WizardModalComponent>;
  public dialogCheckRef: NbDialogRef<WizardCheckToCloseModalComponent>;
  public socket: any;
  public socketSubscription;
  termsSeachInput: any;
  selectedIntegrationPriorToSave: any;
  constructor(
    private translationService: TranslationService,
    private integrationsService: IntegrationService,
    private fb: FormBuilder,
    private colorsService: ColorsService,
    private dialog: NbDialogService,
    private http: HttpUtilService,
    private environmentService: EnvironmentService,
    private notifications: NotificationsService,
    private socketService: WebsocketService
  ) {}

  public openSocket() {
    if (this.socketSubscription?.unsubscribe) {
      this.socketSubscription.unsubscribe();
    }
    this.socket = this.socketService.refreshSocket();
    this.socketSubscription = this.socket.subscribe((socketMessage: any) => {
      if (socketMessage?.Status === "Listener set.") {
        console.log("Listener Set");
      }
      if (socketMessage.type !== "integration") return;
      const integrations = this._integrations.getValue();
      const integration = integrations.list.find(
        (integration) => integration.value.name === socketMessage.integration
      );
      if (!integration) {
        return console.error("no integration found for socket message..");
      }
      const { status, message, updates } = socketMessage;
      const { tokens_process, terms_process } = updates || {};
      if (tokens_process)
        integration.get("tokensProcess").setValue(tokens_process);
      if (terms_process)
        integration.get("termsProcess").setValue(terms_process);
      if (status) integration.get("status").setValue(status);
      if (message || message === "")
        integration.get("message").setValue(message);

      if (status === "ok" || status === "error") {
        integration.get("isProcessing").setValue(false);
        integration.enable();
        if (this.currentPollingProcesses?.[integration?.value?.id]) {
          // stop polling for integration status
          this.currentPollingProcesses[integration.value.id].next();
          delete this.currentPollingProcesses[integration.value.id];
        }
      }
      this._integrations.next(integrations);
      const nowCurrentIntegrations = this._integrations.getValue();
      this.previousIntegrations = cloneDeep(nowCurrentIntegrations.list);
    });
  }

  public getTermsQueryParams({
    pageNumber,
    perPage,
    searchParam,
    searchTerm,
    orderBy,
    direction,
    queryKeys,
    queryValues,
    queryOperations,
    status,
  }) {
    let queryParams = `mode=list&output=dataonly&key_values=name,start_at,end_at,integration_friendly_name,integration.friendly_name,term_id,last_imported&pageNumber=${pageNumber}&perPage=${perPage}&orderBy=${orderBy}&direction=${direction}&status=${status}&integrationId=${this?._integrations?.value?.selectedIntegration?.value?.id}`;
    if (queryKeys && queryValues && queryOperations) {
      queryParams += `&queryKeys=${queryKeys}&queryValues=${queryValues}&queryOperations=${queryOperations}`;
    }
    if (searchTerm) {
      queryParams += `&searchTerm=${searchTerm}&searchParam=${searchParam}`;
    }
    return queryParams;
  }

  public getTerms$() {
    if (this.loadTerms) {
      this.loadTerms = false;
      this.getTermsNetworkRequest$();
    }
    return this.termsInput$;
  }

  public getTermsNetworkRequest$(input = {}) {
    this._termsInput.next({
      ...this._termsInput.getValue(),
      isProcessing: true,
    });
    this.termsParams = { ...this.termsParams, ...input };

    const queryParams = this.getTermsQueryParams(this.termsParams);
    this.http
      .request(
        "get",
        `${this.environmentService.environment.apiRoot}/terms?${queryParams}`
      )
      .pipe(retry(3))
      .pipe(catchError((error) => of(error)))
      .pipe(
        map((response) =>
          !response?.id || response?.error
            ? response
            : { totalItems: 1, listItems: [response] }
        )
      )
      .subscribe((response) => {
        if (response.listItems) {
          this._termsInput.next({
            ...this._termsInput.getValue(),
            itemCount: response.totalItems,
            isProcessing: false,
            hasErrors: false,
            list: response.listItems,
          });
        }
      });
  }

  public updateIntegation(name, updates, index, justFriendlyNameUpdate, id) {
    // Avoid if we are not just using canvas here and not just for lms
    updates = { ...updates, lms_type: "canvas" };
    if (!justFriendlyNameUpdate) {
      updates.terms_process = { status: "processing", message: "" };
      updates.tokens_process = { status: "processing", message: "" };
      updates.status = "processing";
      updates.message = "";
    }
    this.integrationsService
      .updateIntegrationNetworkCall("lms", name, updates, null, null, id)
      .subscribe((response) => {
        const currentValue = this._integrations.getValue();
        let integrationForm = currentValue.list[index];
        if (integrationForm?.name !== name) {
          integrationForm = currentValue.list.find(
            (integration) => integration.value.name === name
          );
        }
        if (response?.[0]?.id && integrationForm) {
          // success
          if (
            updates?.hasOwnProperty?.("friendly_name") &&
            integrationForm?.value?.friendlyNameDisplayed
          ) {
            integrationForm
              ?.get("friendlyNameDisplayed")
              ?.setValue(updates?.friendly_name);
          }

          const message = justFriendlyNameUpdate
            ? this.translationService.getTranslatedContent(
                "updated_integration",
                "Updated Integration"
              )
            : this.translationService.getTranslatedContent(
                "updated_integration._getting_tokens_and_terms",
                "Updated Integration. Getting tokens and terms"
              );
          this.notifications.popNotification("", message);
          this.startPollingIntegration(response[0].id, integrationForm);
          if (justFriendlyNameUpdate) {
            integrationForm?.get("status")?.setValue("ok");
            integrationForm?.get("message")?.setValue("");
            integrationForm?.get("isProcessing")?.setValue(false);
            integrationForm.enable();
          }
          const nowCurrentIntegrations = this._integrations.getValue();
          this.previousIntegrations = cloneDeep(nowCurrentIntegrations.list);
        } else {
          // error
          const errorMessage =
            response?.errorMessage || "Unknown Error Updating Integration";
          integrationForm.enable();
          integrationForm.get("isProcessing").setValue(false);
          integrationForm.get("status").setValue("error");
          integrationForm.get("message").setValue(errorMessage);
          if (justFriendlyNameUpdate) {
            integrationForm?.get("status")?.setValue("error");
            integrationForm
              ?.get("message")
              ?.setValue("Not a Unique Friendly Name");
          }
          if (!justFriendlyNameUpdate) {
            integrationForm
              .get("tokensProcess")
              .setValue({ message: errorMessage, status: "error" });
            integrationForm
              .get("termsProcess")
              .setValue({ message: errorMessage, status: "error" });
          }
          integrationForm?.get("isProcessing")?.setValue(false);
          integrationForm.enable();
          this.notifications.popNotification(
            "",
            "Error Updating Integration: " + errorMessage
          );
        }
      });
  }

  public startPollingIntegration(id, integrationForm) {
    // wait 10 seconds and then every 10 seconds, attempt to get the status of the integration 30 times
    const stopPolling$ = new Subject();
    this.currentPollingProcesses[id] = stopPolling$;

    interval(10000)
      .pipe(
        delay(10000),
        take(30),
        switchMap(() =>
          this.integrationsService.getIntegrationNetworkCall$(id)
        ),

        takeUntil(stopPolling$)
      )
      .subscribe((integration) => {
        const integrationFormValue = integrationForm.value;
        if (integration && integrationFormValue.status === "processing") {
          if (integration.json_data.terms_process) {
            integrationForm
              .get("termsProcess")
              .setValue(integration.json_data.terms_process);
          }
          if (integration.json_data.tokens_process) {
            integrationForm
              .get("tokensProcess")
              .setValue(integration.json_data.tokens_process);
          }
          if (integration.json_data.status) {
            integrationForm
              .get("status")
              .setValue(integration.json_data.status);
          }
          if (integration.json_data.message) {
            integrationForm
              .get("message")
              .setValue(integration.json_data.message);
          }
        }
        if (
          integration.json_data.status === "ok" ||
          integration.json_data.status === "error" ||
          integrationFormValue.status === "ok" ||
          integrationFormValue.status === "error"
        ) {
          integrationForm.enable();
          integrationForm.get("isProcessing").setValue(false);
          stopPolling$.next();
          delete this.currentPollingProcesses?.[id];
        }
      });
  }

  private checkIfInvalidImportInfo(integration) {
    if (
      (!integration.dateRange.beforeDate && integration.dateRange.afterDate) ||
      (integration.dateRange.beforeDate && !integration.dateRange.afterDate)
    ) {
      return "Range dates must both be filled in";
    }

    if (
      integration.dateRange.beforeDate &&
      integration.dateRange.afterDate &&
      integration.dateRange.afterDate > integration.dateRange.beforeDate
    ) {
      return "Start After date cannot be after Starts Before date";
    }
  }

  public saveImportInfo$(shouldKickoffMassImport, updates) {
    const currentIntegrationValue = this._integrations.getValue();
    const integration = currentIntegrationValue.selectedIntegration;
    const invalid = this.checkIfInvalidImportInfo(integration.value);
    if (invalid) return this.notifications.popNotification("", invalid);
    integration.value.isProcessing = true;
    integration.disable();
    this._integrations.next({
      ...this._integrations.getValue(),
      selectedIntegration: integration,
    });

    this.integrationsService
      .updateIntegrationNetworkCall(
        "lms",
        integration.value.name,
        updates,
        null,
        shouldKickoffMassImport,
        null
      )

      .subscribe((response) => {
        if (response?.[0]?.id) {
          this.selectedIntegrationPriorToSave = cloneDeep(integration?.value);
          const instanceDetails = this._instanceDetails.getValue() as any;
          if (!instanceDetails?.wizard_terms_set) {
            this.updateInstitutionStepAsDone("wizard_terms_set");
          }
          this.notifications.popNotification(
            "",
            shouldKickoffMassImport
              ? "Updated Integration and Starting Import"
              : "Updated Integration"
          );
        } else {
          this.notifications.popNotification("", "Error Updating Integration");
        }
        integration.value.isProcessing = false;
        integration.enable();
        this._integrations.next({
          ...this._integrations.getValue(),
          selectedIntegration: integration,
        });
      });
  }

  public getIntegrations$() {
    if (this.loadIntegrations) {
      this.loadIntegrations = false;
      this.getIntegrationsNetworkRequest();
    }
    return this.integrations$;
  }

  public selectIntegration(selectedIntegration) {
    const previousIntegration = this.previousIntegrations.find(
      (integration) => integration?.value?.id === selectedIntegration?.value?.id
    );
    this.selectedIntegrationPriorToSave = cloneDeep(previousIntegration?.value);
    this._integrations.getValue();
    this._integrations.next({
      ...this._integrations.getValue(),
      selectedIntegration,
    });
    this.getTermsNetworkRequest$();
  }

  private getIntegrationsNetworkRequest() {
    this.http
      .request(
        "get",
        `${this.environmentService.environment.apiRoot}/integrations?read=json_data&created=true`
      )
      .pipe(retry(3))
      .pipe(catchError((error) => of({ error })))
      .subscribe((response) => {
        if (Array.isArray(response)) {
          const integrations = response.map((integration) => {
            const isProcessing =
              integration?.json_data?.status === "processing";
            const form = this.fb.group({
              id: new FormControl(integration.id),
              searchTerms: new FormControl(
                integration?.json_data?.searchTerms || []
              ),
              dateRange: {
                afterDate: integration?.json_data?.dateRange?.afterDate || "",
                beforeDate: integration?.json_data?.dateRange?.beforeDate || "",
              },
              isProcessing: new FormControl(isProcessing),
              friendly_name: new FormControl(
                integration?.json_data?.friendly_name,
                [Validators.required]
              ),
              friendlyNameDisplayed: new FormControl(
                integration?.json_data?.friendly_name,
                []
              ),
              name: new FormControl(integration?.name),
              url: new FormControl(integration?.json_data?.url, [
                Validators.required,
                Validators.pattern('^(https)://[^ "]+$'),
              ]),
              utilUsername: new FormControl(
                integration?.json_data?.utilUsername,
                [Validators.required]
              ),
              utilPassword: new FormControl(
                integration?.json_data?.utilPassword,
                [Validators.required]
              ),
              status: new FormControl(integration?.json_data?.status),
              message: new FormControl(integration?.json_data?.message),
              termsToPull: new FormControl(integration?.json_data?.termsToPull),
              termsProcess: new FormControl(
                integration?.json_data?.terms_process || []
              ),
              tokensProcess: new FormControl(
                integration?.json_data?.tokens_process
              ),
            });
            if (isProcessing) form.disable();
            return form;
          });
          const selectedIntegration =
            integrations?.length === 1
              ? integrations[0]
              : this._integrations.getValue()?.selectedIntegration;
          this.previousIntegrations = cloneDeep(integrations);
          this.selectedIntegrationPriorToSave = cloneDeep(
            selectedIntegration?.value
          );
          this._integrations.next({
            isLoading: false,
            list: integrations,
            selectedIntegration,
          });
        }
      });
  }

  public updateWizardStepVisited(
    step: "integration" | "theme" | "users" | "terms"
  ) {
    let state =
      step === "integration"
        ? this._integrationInput
        : step === "theme"
        ? this._themeInput
        : step === "users"
        ? this._usersInput
        : this._termsInput;
    state.next({
      ...state.getValue(),
      hasVisited: true,
    } as any);
  }
  public getInstanceDetails$() {
    if (this.loadInstanceDetails) {
      this.loadInstanceDetails = false;
      this.environmentService
        .getInstanceDetails()
        .pipe(take(1))
        .subscribe((response) => {
          this._instanceDetails.next(response);
        });
    }

    return this._instanceDetails.asObservable();
  }

  public openWizard() {
    this.dialogRef = this.dialog.open(WizardModalComponent, {
      closeOnBackdropClick: false,
      closeOnEsc: false,
    });
  }

  public getRoles$() {
    return this.http
      .request(
        "get",
        this.environmentService.environment.apiRoot +
          "/roles?output=dataonly&mode=list&key_values=name,title,prevent_edit"
      )
      .pipe(retry(2))
      .pipe(catchError((error) => of({ error })))
      .pipe(
        map((response) =>
          response?.data ? { listItems: [response] } : response
        )
      );
  }

  public getUsers$() {
    return this.http
      .request(
        "get",
        this.environmentService.environment.apiRoot +
          "/users?output=dataonly&mode=list&queryKeys=lti_created_user&queryValues=false&queryOperations=notAvailable&queryOperand=and&key_values=cognito_username,email"
      )
      .pipe(catchError((error) => of({ error })))
      .pipe(
        map((response) =>
          response?.data ? { listItems: [response] } : response
        )
      );
  }

  public getTheme$() {
    this.colorsService.getTheme$().subscribe((response) => {
      if (response.isLoading) return;

      this._themeInput.next({
        ...this._themeInput.getValue(),
        input: response.theme as any,
      });
    });
  }

  public updateIntegrationInput(input) {
    this._integrationInput.next({
      ...this._integrationInput.getValue(),
      input,
    });
  }

  public getNewIntegrationName() {
    const instanceDetails = this.environmentService.environment.domain.replace(
      /[^a-zA-Z0-9]/g,
      ""
    );
    const integrations = this._integrations.getValue();

    return `${instanceDetails.substring(0, 7)}_${integrations.list.length + 1}`;
  }

  public createIntegration() {
    const currentValue = this._integrationInput.getValue();
    this._integrationInput.next({
      ...currentValue,
      isProcessing: true,
      status: "processing",
    });
    const formValues = currentValue.input.getRawValue();
    const { friendlyName, url, username, password } = formValues;
    const name = this.getNewIntegrationName();
    const data = {
      integration_type: "lms",
      friendly_name: friendlyName.trim(),
      utilUsername: username.trim(),
      utilPassword: password.trim(),
      url: url.trim(),
      lms_type: "canvas",
      sub_account_id: 1,
      data_direction: "source",
    };
    return this.createIntegrationNetworkCall(name, data).pipe(
      tap((response) => {
        currentValue.input.enable();
        if (response.id) {
          const instanceDetails = this._instanceDetails.getValue() as any;

          const integration = this.fb.group({
            id: new FormControl(response.id),
            isProcessing: new FormControl(true),
            searchTerms: new FormControl([]),
            dateRange: {
              afterDate: "",
              beforeDate: "",
            },
            friendly_name: new FormControl(friendlyName.trim(), [
              Validators.required,
            ]),
            termsProcess: new FormControl({
              status: "processing",
              message: "Yet to start",
            }),
            tokensProcess: new FormControl({
              status: "processing",
              message: "Yet to start",
            }),
            friendlyNameDisplayed: new FormControl(friendlyName.trim(), []),
            name: new FormControl(response.name),
            url: new FormControl(url.trim(), [
              Validators.required,
              Validators.pattern('^(https)://[^ "]+$'),
            ]),
            utilUsername: new FormControl(username.trim(), [
              Validators.required,
            ]),
            utilPassword: new FormControl(password.trim(), [
              Validators.required,
            ]),
            status: new FormControl("processing"),
            message: new FormControl("Initial Step"),
            termsToPull: new FormControl([]),
          });
          const integrationsCurrentValue = this._integrations.getValue();
          const list = [integration, ...integrationsCurrentValue.list];
          integration.disable();
          this._integrations.next({
            isLoading: false,
            list,
            selectedIntegration:
              list.length === 1
                ? integration
                : this._integrations?.getValue()?.selectedIntegration,
          });
          const nowCurrentIntegrations = this._integrations.getValue();
          this.previousIntegrations = cloneDeep(nowCurrentIntegrations.list);
          this.notifications.popNotification(
            "",
            this.translationService.getTranslatedContent(
              "created_integration_getting_tokens_and_terms_lingo",
              "Created Integration. Getting Tokens and Terms"
            )
          );
          currentValue.input.reset();
          this._integrationInput.next({
            ...this._integrationInput.getValue(),
            isProcessing: false,
            hasUpdates: false,
            hasErrors: false,
            hasVisited: true,
            status: this.translationService.getTranslatedContent(
              "standby",
              "Standby"
            ),
          });

          this.startPollingIntegration(response.id, integration);

          if (!instanceDetails?.wizard_integration_set) {
            this.updateInstitutionStepAsDone("wizard_integration_set");
          }
        } else {
          const message =
            response.errorMessage ||
            response?.error?.message ||
            response?.error?.errorMessage ||
            response.error.error ||
            response.error;
          // response bad
          this.notifications.popNotification(
            "",
            `${this.translationService.getTranslatedContent(
              "error_creating_integration",
              "Error Creating Integration"
            )} : ${message} `
          );
          this._integrationInput.next({
            ...this._integrationInput.getValue(),
            isProcessing: false,
            hasUpdates: false,
            hasErrors: true,
            hasVisited: true,
            status: this.translationService.getTranslatedContent(
              "error",
              "Error"
            ),
          });
        }
      })
    );
  }

  createIntegrationNetworkCall(name, data, partnerId = null) {
    let integrationUrl = `${
      this.environmentService.environment.apiRoot
    }/integrations/lms/${name}${!partnerId ? "" : `?partnerId=${partnerId}`}`;

    return this.http
      .request("post", integrationUrl, { json_data: data }, null, true)
      .pipe(catchError((error) => of({ error })));
  }

  updateThemeInput(input) {
    const currentValue = this._themeInput.getValue();
    this._themeInput.next({ ...currentValue, input, hasUpdates: true });
  }

  updateTheme$(body) {
    const currentValue = this._themeInput.getValue();
    this._themeInput.next({
      ...currentValue,
      input: { ...currentValue.input, ...body.colors },
      isProcessing: true,
    });
    const update = { ...currentValue.input, ...body };

    return this.colorsService.updateTheme$(update).pipe(
      tap((response) => {
        const currentValue = this._themeInput.getValue();
        this._themeInput.next({
          ...currentValue,
          isProcessing: false,
          hasUpdates: false,
        });

        if (response === "SUCCESS") {
          this.notifications.popNotification(
            "",
            this.translationService.getTranslatedContent(
              "the_theme_has_been_updated_lingo",
              "The theme has been updated. It will take a few minutes for the updates to be reflected."
            )
          );
          const instanceDetails = this._instanceDetails.getValue() as any;
          if (!instanceDetails?.wizard_theme_set) {
            this.updateInstitutionStepAsDone("wizard_theme_set");
          }
        } else {
          this.notifications.popNotification(
            "",
            this.translationService.getTranslatedContent(
              "error_updating_theme_at_this_time.",
              "Error Updating Theme at this time."
            )
          );
        }
      })
    );
  }

  createUserAccountNetworkRequest$({ email, role, username }) {
    const body = { type: "users", data: { username, email, role } };
    return this.http
      .request(
        "POST",
        `${this.environmentService.environment.apiRoot}/users`,
        body
      )
      .pipe(catchError((error) => of({ error })));
  }

  createUserAccounts$(userAccounts) {
    this._usersInput.next({
      ...this._usersInput.getValue(),
      isProcessing: true,
    });
    const usersCreate$ = from(userAccounts).pipe(
      concatMap((userAccount: any) =>
        this.createUserAccountNetworkRequest$(userAccount.getRawValue())
      ),
      toArray()
    );

    return usersCreate$
      .pipe(
        tap((responses) => {
          userAccounts.forEach((control, index) => {
            if (control.get("status").value === "Created") return;
            const userCreateResponse = responses[index] as any;
            const status = !!userCreateResponse?.id ? "Created" : "Error";
            const statusMessage = !!userCreateResponse?.id
              ? ""
              : userCreateResponse?.errorMessage ||
                userCreateResponse?.message ||
                "Unknown Error";
            control.get("status").setValue(status);
            control.get("statusMessage").setValue(statusMessage);
            status === "Created" ? control.disable() : control.enable();
          });
          this._usersInput.next({
            hasErrors: false,
            hasUpdates: false,
            input: this.usersFormArray,
            isProcessing: false,
            hasVisited: true,
          });
        })
      )
      .pipe(
        tap((responses: any) => {
          const instanceDetails = this._instanceDetails.getValue() as any;
          if (
            !instanceDetails.wizard_users_set &&
            responses.find((response) => !!response["id"])
          ) {
            this.updateInstitutionStepAsDone("wizard_users_set");
          }
        })
      );
  }

  updateInstitutionStepAsDone(
    step:
      | "wizard_theme_set"
      | "wizard_integration_set"
      | "wizard_users_set"
      | "wizard_terms_set"
  ) {
    this.http
      .request(
        "put",
        `${this.environmentService.environment.apiRoot}/institution/fields`,
        {
          [step]: true,
        }
      )
      .subscribe((response) => {
        if (response === "Success") {
          const currentValue = this._instanceDetails.getValue();
          this._instanceDetails.next({ ...currentValue, [step]: true });
        }
      });
  }

  resetTermsState() {
    this.loadTerms = true;
    this.termsFormArray = this.fb.array([]);
    this._termsInput.next({
      itemCount: 0,
      isLoading: false,
      isProcessing: false,
      hasUpdates: false,
      hasErrors: false,
      hasVisited: false,
      list: this.termsFormArray as any,
    });
  }

  resetIntegrationState() {
    this.loadIntegrations = true;
    this._integrations.next({
      isLoading: true,
      list: [],
      selectedIntegration: null,
    });
  }

  closeModalState() {
    Object.keys(this.currentPollingProcesses).forEach((id) => {
      this.currentPollingProcesses?.[id]?.next();
      delete this.currentPollingProcesses?.[id];
    });
    this.filterOutCreatedUsers();
    this.resetIntegrationState();
    this.resetTermsState();
  }

  filterOutCreatedUsers() {
    let usersState = this._usersInput.getValue();
    const newUsersState = usersState.input.controls.filter(
      (control) => control.get("status").value !== "Created"
    );
    this._usersInput.next({
      ...usersState,
      input: this.fb.array(newUsersState),
    });
  }

  checkForChangesBeforeClose() {
    const integrationState = this._integrationInput.getValue();
    const themeState = this._themeInput.getValue();
    let usersState = this._usersInput.getValue();
    let importInfoChanges = this.getImportInfoChanges();
    return integrationState.hasUpdates || !integrationState.input.pristine
      ? { step: 0 }
      : themeState.hasUpdates
      ? { step: 1 }
      : usersState?.input.controls.filter(
          (control) => control.get("status").value !== "Created"
        ).length
      ? { step: 2 }
      : importInfoChanges
      ? { step: 3 }
      : null;
  }

  compareArrays(previousState, currentState) {
    if (!previousState || !currentState) return false;
    if (previousState.length !== currentState.length)
      return previousState.length > currentState.length
        ? "removed"
        : "addition";
    const sortedPreviousState = previousState.sort();
    const sortedCurrentState = currentState.sort();
    for (let i = 0; i < previousState.length; i++) {
      if (!isEqual(sortedPreviousState[i], sortedCurrentState[i]))
        return "updated";
    }
    return false;
  }

  compareObjects(previousState, currentState) {
    if (!previousState || !currentState) return false;
    return isEqual(previousState, currentState)
      ? false
      : Object.keys(currentState).every((key) => !currentState[key])
      ? "removed"
      : "updated";
  }

  getTermChanges() {
    const previousState = this?.selectedIntegrationPriorToSave;
    const currentState =
      this?._integrations?.getValue()?.selectedIntegration?.value;
    return this.compareArrays(
      previousState?.termsToPull?.map(({ id }) => String(id)),
      currentState?.termsToPull?.map(({ id }) => String(id))
    );
  }

  getCourseCodeChanges() {
    const previousState = this?.selectedIntegrationPriorToSave;
    const currentState =
      this?._integrations?.getValue()?.selectedIntegration?.value;
    return this.compareArrays(
      previousState?.searchTerms,
      currentState?.searchTerms
    );
  }

  getDateChanges() {
    const previousState = this?.selectedIntegrationPriorToSave;
    const currentState =
      this?._integrations?.getValue()?.selectedIntegration?.value;
    return this.compareObjects(
      previousState?.dateRange,
      currentState?.dateRange
    );
  }

  getImportInfoChanges() {
    const dateChanges = this.getDateChanges();
    if (dateChanges === "updated") return dateChanges;
    const courseCodeMatchChanges = this.getCourseCodeChanges();
    if (
      courseCodeMatchChanges === "updated" ||
      courseCodeMatchChanges === "addition"
    ) {
      return courseCodeMatchChanges;
    }
    const termChanges = this.getTermChanges();
    if (termChanges === "updated" || termChanges === "addition")
      return termChanges;

    return dateChanges === "removed"
      ? dateChanges
      : courseCodeMatchChanges === "removed"
      ? courseCodeMatchChanges
      : termChanges;
  }
}
